/**
 * @better-bookkeeping/auth-client
 * Frontend authentication client for Abacus Platform
 * Combines Better-Auth client with oRPC typed client
 */
import { createAuthClient } from "better-auth/client";
import { organizationClient, phoneNumberClient, twoFactorClient } from "better-auth/client/plugins";
import { passkeyClient } from "@better-auth/passkey/client";
import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import { createConsumerJwtManager, } from "./jwt-manager.js";
export { ConsumerJwtUnavailableError, createAuthenticatedUserFetch, createBrowserJwtStorage, createConsumerJwtManager, } from "./jwt-manager.js";
/**
 * Better-Auth client instance
 * Handles authentication flows, session management, and OAuth
 */
/**
 * Custom redirect plugin that resolves redirect URLs against the auth gateway.
 * Better Auth's default redirect plugin uses relative URLs (e.g. "/login") which
 * resolve against the current domain — wrong for consuming apps on different domains.
 * This plugin rewrites relative redirects to point to the auth gateway, and appends
 * redirect_uri so the gateway sends the user back after login.
 */
function createGatewayRedirectPlugin(gatewayBaseUrl) {
    return {
        id: "gateway-redirect",
        name: "Gateway Redirect",
        hooks: {
            onSuccess(context) {
                if (!context.data?.url || !context.data?.redirect)
                    return;
                if (typeof window === "undefined")
                    return;
                let targetUrl = context.data.url;
                // For any relative redirect (e.g. "/login"), send to the gateway root instead.
                // The gateway's index route handles the full flow: session check → login →
                // security setup → redirect back. Following the server's relative path directly
                // can cause hydration issues in the gateway SPA.
                if (targetUrl.startsWith("/")) {
                    const returnUrl = encodeURIComponent(window.location.href);
                    targetUrl = `${gatewayBaseUrl}?redirect_uri=${returnUrl}`;
                }
                try {
                    window.location.href = targetUrl;
                }
                catch {
                    // ignore navigation errors
                }
            },
        },
    };
}
/**
 * Better-Auth client instance
 * Handles authentication flows, session management, and OAuth
 */
export function createBetterAuthClient(config) {
    return createAuthClient({
        baseURL: config.baseUrl,
        // Disable built-in redirect plugin — it resolves relative URLs against the current
        // domain, which breaks cross-origin consuming apps. Our gateway redirect plugin
        // resolves them against the auth gateway instead.
        disableDefaultFetchPlugins: true,
        fetchOptions: {
            customFetchImpl: config.fetchFn,
            plugins: [createGatewayRedirectPlugin(config.baseUrl)],
        },
        plugins: [organizationClient(), twoFactorClient(), passkeyClient(), phoneNumberClient()],
    });
}
/**
 * Create a typed oRPC API client that speaks the RPCHandler wire protocol.
 * Uses RPCLink so all calls are properly serialized — no REST shims needed.
 */
export function createORPCAPIClient(config) {
    const apiUrl = `${config.baseUrl}${config.apiPath || "/api/v1"}`;
    const fetchFn = config.fetchFn || fetch;
    const link = new RPCLink({
        url: apiUrl,
        fetch: (input, init) => fetchFn(input, { ...init, credentials: "include" }),
    });
    return createORPCClient(link);
}
/**
 * Unified authentication client
 * Combines Better-Auth and oRPC clients
 */
export class AuthClient {
    /**
     * Base URL of the auth gateway (used for redirect-based flows)
     */
    baseUrl;
    /**
     * Better-Auth client for authentication flows
     */
    auth;
    /**
     * oRPC client for type-safe API calls
     */
    api;
    constructor(config) {
        this.baseUrl = config.baseUrl;
        this.auth = createBetterAuthClient(config);
        this.api = createORPCAPIClient(config);
    }
    /**
     * Sign in with email and password
     */
    async signIn(email, password) {
        return this.auth.signIn.email({
            email,
            password,
        });
    }
    /**
     * Sign up with email and password
     */
    async signUp(email, password, name) {
        return this.auth.signUp.email({
            email,
            password,
            name: name || "",
        });
    }
    /**
     * Sign out
     */
    async signOut() {
        return this.auth.signOut();
    }
    /**
     * Get current session
     */
    async getSession() {
        return this.auth.getSession();
    }
    /**
     * Sign in with OAuth provider
     */
    async signInWithOAuth(provider) {
        return this.auth.signIn.social({
            provider,
        });
    }
    /**
     * Reset password with token (Better Auth built-in)
     */
    async resetPassword(token, newPassword) {
        return this.auth.resetPassword({
            newPassword,
            token,
        });
    }
    /**
     * Create JWT token for API calls
     */
    async createJWT(expiresIn) {
        return this.api.auth.createJWT({
            expiresIn,
        });
    }
    createJwtManager(config = {}) {
        const { expiresIn = 900, ...managerConfig } = config;
        return createConsumerJwtManager({
            ...managerConfig,
            fetchToken: () => this.api.auth.createJWT({ expiresIn }),
        });
    }
    /**
     * Get user's organizations
     */
    async getOrganizations() {
        return this.api.organization.listOrganizations();
    }
    /**
     * Create new organization
     */
    async createOrganization(name, slug) {
        return this.api.organization.createOrganization({
            name,
            slug,
        });
    }
    /**
     * Set active organization
     */
    async setActiveOrganization(organizationId) {
        // Update via Better-Auth
        await this.auth.organization.setActive({
            organizationId,
        });
        return this.api.organization.setActiveOrganization({
            organizationId,
        });
    }
    /**
     * Invite member to organization
     */
    async inviteMember(email, role = "member") {
        return this.api.organization.inviteMember({
            email,
            role,
        });
    }
    /**
     * Get organization members
     */
    async getMembers(organizationId) {
        return this.api.organization.listMembers({
            organizationId,
        });
    }
    /**
     * Accept organization invitation
     */
    async acceptInvitation(invitationId) {
        return this.api.organization.acceptInvitation({
            invitationId,
        });
    }
    /**
     * Enable two-factor authentication
     * TODO(AUTH-5): Fix Better-Auth API - enable() requires password parameter
     */
    async enableTwoFactor(password) {
        return this.auth.twoFactor.enable({
            password,
        });
    }
    /**
     * Disable two-factor authentication
     */
    async disableTwoFactor(password) {
        return this.auth.twoFactor.disable({
            password,
        });
    }
    /**
     * Verify two-factor code
     * TODO(AUTH-5): Fix Better-Auth API - verify() method doesn't exist on twoFactor
     */
    async verifyTwoFactor(code) {
        return this.auth.twoFactor.verifyTotp({
            code,
        });
    }
    /**
     * Register a new passkey for the authenticated user
     */
    async addPasskey(name) {
        return this.auth.passkey.addPasskey({
            name,
        });
    }
    /**
     * Sign in with a passkey (passwordless)
     */
    async signInWithPasskey(autoFill) {
        return this.auth.signIn.passkey({
            autoFill,
        });
    }
    /**
     * List user's registered passkeys
     */
    async listPasskeys() {
        return this.auth.passkey.listUserPasskeys();
    }
    /**
     * Delete a passkey
     */
    async deletePasskey(id) {
        return this.api.auth.deletePasskey({ id });
    }
    /**
     * List user's active sessions
     */
    async getSessions() {
        return this.api.auth.listSessions();
    }
    /**
     * Revoke a specific session
     */
    async revokeSession(sessionId) {
        return this.api.auth.revokeSession({ sessionId });
    }
    /**
     * Revoke all sessions except current
     */
    async revokeAllSessions() {
        return this.api.auth.revokeOtherSessions();
    }
}
/**
 * Create unified authentication client
 */
export function createAbacusAuthClient(config) {
    return new AuthClient(config);
}
